import React, { useMemo } from 'react';
import { faRefresh, faAdd, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionToolbar from '../common/ActionToolbar';

/**
 * InquiryActions
 * Minimal action toolbar for inquiries page: search, refresh, add
 */
const InquiryActions = ({
  searchValue = '',
  onSearchChange,
  onAddInquiry,
  onRefresh,
  onBulkDelete,
  hasSelection = false,
  selectionCount = 0,
  loading = false,
  refreshLoading = false,
  showSearch = true,
  fromDate = '',
  toDate = '',
  onFromDateChange,
  onToDateChange,
}) => {
  const { t } = useTranslation();
  const filters = useMemo(() => ([
    {
      key: 'from-date',
      type: 'date',
      label: t('commonCols.from'),
      value: fromDate,
      onChange: onFromDateChange,
    },
    {
      key: 'to-date',
      type: 'date',
      label: 'To',
      value: toDate,
      onChange: onToDateChange,
    },
  ]), [fromDate, toDate, onFromDateChange, onToDateChange]);

  const actions = useMemo(() => {
    const baseActions = [
      {
        key: 'refresh-inquiries',
        label: t('inquiriesModule.inquiries.actions.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading || loading,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'add-inquiry',
        label: t('inquiriesModule.inquiries.actions.buttons.add'),
        icon: faAdd,
        onClick: onAddInquiry,
        disabled: loading || refreshLoading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
      },
    ];

    // Add bulk delete action when there's a selection
    if (hasSelection && onBulkDelete) {
      baseActions.unshift({
        key: 'bulk-delete',
        label: `Delete Selected (${selectionCount})`,
        icon: faTrash,
        onClick: onBulkDelete,
        disabled: loading || refreshLoading,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    return baseActions;
  }, [onAddInquiry, onRefresh, onBulkDelete, hasSelection, selectionCount, loading, refreshLoading, t]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('inquiriesModule.inquiries.actions.searchPlaceholder')}
      actions={actions}
      filters={filters}
    />
  );
};

export default InquiryActions;
